import type { AvailabilityWindowListLabels } from '../components/availability-window-list';
import type { ReservationListViewLabels } from '../components/reservation-list-view';
import type { ReservationQueueListLabels } from '../components/reservation-queue-list';
import type {
  UnitQueueLabels,
  UnitReservationsTabLabels,
} from '../components/unit-reservations-tab';

type Translate = (key: string, opts?: Record<string, unknown>) => string;
export type TranslateWithDefault = (key: string, opts?: Record<string, unknown> | string) => string;

export function buildReservationListLabels(t: Translate): ReservationListViewLabels {
  return {
    pageTitle: t('pageTitle'),
    statusLabel: t('columns.status'),
    mediaUnitLabel: t('columns.mediaUnit'),
    dateFromLabel: t('columns.dateFrom'),
    dateToLabel: t('columns.dateTo'),
    holderLabel: t('queueOverview.holderColumnLabel'),
    holderInlineLabel: t('queueOverview.holderInlineLabel'),
    offerLabel: t('queueOverview.offerColumnLabel'),
    sourceLabel: t('columns.source'),
    queuedLabel: t('columns.queued'),
    queuedTooltipLabel: t('columns.queuedTooltip'),
    deadlineLabel: t('columns.deadline'),
    deadlineTooltipLabel: t('columns.deadlineTooltip'),
    deadlineNeverExpiresLabel: t('deadline.neverExpires'),
    deadlineNoDeadlineSetLabel: t('deadline.noDeadlineSet'),
    deadlineExpiredMarkerLabel: t('deadline.expiredMarker'),
    actionsLabel: t('columns.actions'),
    noOfferLabel: t('labels.noOffer'),
    openUnitLabel: t('actions.openUnit'),
    openOfferLabel: t('actions.openOffer'),
    upgradeHardLabel: t('actions.upgradeHard'),
    closeSaleLabel: t('actions.closeSale'),
    editDatesLabel: t('actions.editDates'),
    cancelLabel: t('actions.cancel'),
    forceReleaseLabel: t('actions.forceRelease'),
    overrideStatusLabel: t('actions.overrideStatus'),
    blockAvailabilityLabel: t('actions.blockAvailability'),
    extendExpiryLabel: t('actions.extendExpiry'),
    totalCountLabel: (count: number) => t('labels.totalCount', { count }),
    pageLabel: (p: number, total: number) => t('labels.page', { page: p, total }),
    prevPageLabel: t('labels.prevPage'),
    nextPageLabel: t('labels.nextPage'),
  };
}

/** Labels for the expanded FCFS line on the global reservations list. */
export function buildReservationQueueLabels(t: Translate): ReservationQueueListLabels {
  return {
    title: t('unitTab.queueSectionTitle'),
    subtitle: t('unitTab.queueSectionSubtitle'),
    noOfferLabel: t('labels.noOffer'),
    positionLabel: (position: number) => t('unitTab.queuePositionLabel', { position }),
    spansMultipleLabel: t('unitTab.queueSpansMultiple'),
    ownBadgeLabel: t('unitTab.queueOwnBadge'),
    submittedByLabel: t('unitTab.queueSubmittedBy'),
    leaveLabel: t('waitlist.leaveBtn'),
    removeLabel: t('unitTab.queueRemoveLabel'),
    promoteLabel: t('unitTab.queuePromoteLabel'),
    salespersonLabel: t('queueOverview.salespersonLabel'),
    joinedAtLabel: t('queueOverview.joinedAtLabel'),
    promotionEtaLabel: t('queueOverview.promotionEtaLabel'),
    brandLabel: t('queueOverview.brandLabel'),
  };
}

export function buildUnitReservationsTabLabels(t: Translate): UnitReservationsTabLabels {
  return {
    statusLabel: t('columns.status'),
    holderLabel: t('columns.holder'),
    dateFromLabel: t('columns.dateFrom'),
    dateToLabel: t('columns.dateTo'),
    offerLabel: t('columns.offer'),
    sourceLabel: t('columns.source'),
    queuedLabel: t('columns.queued'),
    deadlineLabel: t('columns.deadline'),
    deadlineTooltipLabel: t('columns.deadlineTooltip'),
    deadlineNeverExpiresLabel: t('deadline.neverExpires'),
    deadlineNoDeadlineSetLabel: t('deadline.noDeadlineSet'),
    deadlineExpiredMarkerLabel: t('deadline.expiredMarker'),
    actionsLabel: t('columns.actions'),
    noOfferLabel: t('labels.noOffer'),
    openOfferLabel: t('actions.openOffer'),
    upgradeHardLabel: t('actions.upgradeHard'),
    closeSaleLabel: t('actions.closeSale'),
    editDatesLabel: t('actions.editDates'),
    cancelLabel: t('actions.cancel'),
    forceReleaseLabel: t('actions.forceRelease'),
    overrideStatusLabel: t('actions.overrideStatus'),
    emptyTitle: t('unitTab.emptyState'),
    emptyDescription: t('unitTab.emptyDescription'),
    blockForMaintenanceLabel: t('unitTab.blockForMaintenance'),
    extendExpiryLabel: t('actions.extendExpiry'),
    auditTimelineTitle: t('unitTab.auditTimelineTitle'),
    auditTimelineShowLabel: t('unitTab.auditTimelineShow'),
    auditTimelineHideLabel: t('unitTab.auditTimelineHide'),
    auditTimelineEmptyLabel: t('unitTab.auditTimelineEmpty'),
    systemActorLabel: t('unitTab.system'),
    expandDetailsLabel: t('unitTab.expandDetails'),
    collapseDetailsLabel: t('unitTab.collapseDetails'),
    expiryDeadlineAuditTitle: t('unitTab.expiryDeadlineAudit.title'),
    expiryDeadlineAuditActorLabel: t('unitTab.expiryDeadlineAudit.actorLabel'),
    expiryDeadlineAuditPreviousLabel: t('unitTab.expiryDeadlineAudit.previousLabel'),
    expiryDeadlineAuditNewLabel: t('unitTab.expiryDeadlineAudit.newLabel'),
    expiryDeadlineAuditReasonLabel: t('unitTab.expiryDeadlineAudit.reasonLabel'),
  };
}

export function buildUnitQueueLabels(t: TranslateWithDefault): UnitQueueLabels {
  return {
    title: t('unitTab.queueSectionTitle', 'Kolejka oczekujących'),
    subtitle: t('unitTab.queueSectionSubtitle', 'Kolejność według czasu zgłoszenia'),
    noOfferLabel: t('labels.noOffer'),
    positionLabel: (position: number) =>
      t('unitTab.queuePositionLabel', {
        position,
        defaultValue: `Pozycja ${position} w kolejce`,
      }),
    spansMultipleLabel: t('unitTab.queueSpansMultiple', 'Czeka też na inny okres'),
    ownBadgeLabel: t('unitTab.queueOwnBadge', 'Ty'),
    submittedByLabel: t('unitTab.queueSubmittedBy', 'Zgłosił:'),
    leaveLabel: t('waitlist.leaveBtn'),
    removeLabel: t('unitTab.queueRemoveLabel', 'Usuń z kolejki'),
    promoteLabel: t('unitTab.queuePromoteLabel', 'Przekaż termin'),
    brandLabel: t('queueOverview.brandLabel'),
    expandLabel: (count: number) => t('waitlist.sectionTitle', { count }),
  };
}

export function buildAvailabilityWindowListLabels(t: Translate): AvailabilityWindowListLabels {
  return {
    title: t('availabilityWindow.listTitle'),
    dateFromLabel: t('columns.dateFrom'),
    dateToLabel: t('columns.dateTo'),
    stateLabel: t('availabilityWindow.stateLabel'),
    emptyLabel: t('availabilityWindow.emptyLabel'),
    truncatedNotice: (shown, total) => t('availabilityWindow.truncatedNotice', { shown, total }),
  };
}
